import React, { useEffect } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react-lite';

import { ModalWrapper } from 'shared/modals/v2-wrapper';
import { ModalContainer, Button } from 'components/core';
import { Typography } from 'src/typography';
import useUI from 'hooks/use-ui';
import useDispensary from 'dispensary/hooks/use-dispensary';
import useUser from 'hooks/use-user';
import { ModalComponentProps } from 'components/modals/with-modal-controls-hook';

import { useModals } from '../use-modals';
import { ModalNames } from '../modals.types';
import { OnOpenDataValue } from './exclusive-menu-gate-controller';

export const ExclusiveMenuGateModal = observer(
  ({ onClose, containerProps, data }: ModalComponentProps<OnOpenDataValue>): JSX.Element => {
    const modals = useModals();
    const UI = useUI();
    const { dispensary } = useDispensary();
    const { isLoggedIn } = useUser();

    const handleLoginClick = (): void => {
      void modals.openModal(ModalNames.login);
    };

    const handleContinueShopping = (): void => {
      onClose();
      UI.showMenu(dispensary?.cName);
    };

    const isAccessibleToCurrentUser = data.isAccessibleToCurrentUser ?? false;
    const showRequirementsNotMet = isLoggedIn && !isAccessibleToCurrentUser;
    const message = showRequirementsNotMet
      ? 'Requirements not met to view this collection.'
      : 'Log in to view this exclusive collection.';
    const buttonText = showRequirementsNotMet ? 'Continue Shopping' : 'Log In';
    const buttonAction = showRequirementsNotMet ? handleContinueShopping : handleLoginClick;

    useEffect(() => {
      if (isLoggedIn && isAccessibleToCurrentUser) {
        onClose();
      }
    }, [isLoggedIn, isAccessibleToCurrentUser, onClose]);

    return (
      <ModalWrapper disableBackdropClick disableEscapeKeyDown open>
        <ModalContainer
          data-testid='exclusive-menu-gate-modal-container'
          width='90vw'
          maxWidth='500px'
          padding='32px'
          {...containerProps}
        >
          <Container>
            <Title tag='h2' size='medium'>
              Exclusive Collection
            </Title>

            <Message tag='p' size='medium'>
              {message}
            </Message>
            <LoginButton onClick={buttonAction} variant='primary' fluid>
              {buttonText}
            </LoginButton>
            {!showRequirementsNotMet && <ContinueLink onClick={handleContinueShopping}>Continue Shopping</ContinueLink>}
          </Container>
        </ModalContainer>
      </ModalWrapper>
    );
  }
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 16px;
`;

const Title = styled(Typography.Heading)`
  margin: 0;
  color: var(--dcm-color-text-primary);
  font-size: 20px;
`;

const Message = styled(Typography.Body)`
  margin: 0;
  color: var(--dcm-modal-secondary-text-color, ${({ theme }) => theme.colors.grey[45]});
  font-size: 16px;
`;

const LoginButton = styled(Button)`
  margin-top: 8px;
  border-radius: 24px;
  max-width: 264px;
`;

const ContinueLink = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primaryBlue};
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  padding: 8px;
  text-decoration: none;

  /* Concierge's dark theme swaps the marketplace-wide link blue for its gold accent.
     --dcm-color-primary is always defined (it's the tenant's own brand color outside
     Concierge), so a var() fallback wouldn't work here — this needs an explicit
     selector override scoped to .concierge-mode instead. */
  .concierge-mode & {
    color: var(--dcm-color-primary);
  }

  &:hover {
    text-decoration: underline;
  }
`;
